/**
 * Colours for the allocation bar, one per agency, taken from each agency's own
 * logo so the bar reads the same way as their material.
 *
 * **Keyed on the agency's display name, not its code.** That is what the API
 * returns: `dca_allocation_configuration_v2.dca` is a foreign key to
 * `dca_configuration_v2.dca`, which holds `Pastdue Credit`, `Digital DRA`,
 * `First Locate` and so on. The Junifer ticket codes (`APDC`, `DigitalDRA`,
 * `FirstLocate`) live in a separate column and never reach this screen. Keying on
 * codes made three of six agencies fall through to the grey fallback, and made the
 * "add an agency" picker offer values the foreign key would reject.
 *
 * The trade-offs below are recorded so they are not silently re-broken later.
 *
 * Every pair is at least 11.5 ΔE apart and no two agencies that share a cell are
 * close, but two sit at one end of the lightness range and need the hairline on
 * `.dca-cell__bar-part` to read against their card:
 *
 *  - **Coeo #00FF80** is near-fluorescent: 1.3:1 against a white cell, so on the
 *    light theme the fill barely separates from the card. Fine on dark, 12.9:1.
 *  - **First Locate #262161** is the mirror of that - 14.3:1 on white but
 *    **1.2:1 against the dark surface**, so on the dark theme it reads as a hole
 *    rather than a bar.
 *
 * A per-theme lightened First Locate was tried and rejected: anything light
 * enough for the dark theme (≥3:1, so #675FC9 or above) lands within 13 ΔE of
 * Pastdue Credit, trading a contrast problem for a collision. The hairline fixes
 * both cases without touching either brand colour.
 *
 * First Locate #262161 also sits 11.5 ΔE from Moriarty #4B327A - the closest
 * remaining pair. They never share a row: Moriarty renders only as the gate on
 * the Known segments, and First Locate only appears in Occupier ones, which have
 * no gate. They meet in the legend only.
 *
 * Legibility beats fidelity: if two agencies' brand colours clash on the same
 * bar, change one of them.
 */
export const AGENCY_COLOURS: Record<string, string> = {
  "Pastdue Credit": "#2F60C3",
  Coeo: "#00FF80",
  Opos: "#1E8ECF",
  "First Locate": "#262161",
  Conexus: "#993399",
  "Digital DRA": "#9D31FF",
  Moriarty: "#4B327A",
  // Credit Style is deliberately absent. It is in dca_configuration_v2 and was used
  // historically, but it has never received a placement in outbound_dca.placement_dca
  // and is not used now, so listing it put an entry in the legend pointing at nothing -
  // in a placeholder colour rather than its own, which was never obtained. It stays out
  // of the picker for the same reason. Its name alias below is kept on purpose: a change
  // history entry can still reference it, and it must render as "Credit Style" rather
  // than the raw code. Add a colour here if it is ever brought back into a split.
}

/**
 * The stored value is already the display name, so this is an identity for current
 * data. The code aliases remain because older rows and hand-written fixtures still
 * carry Junifer ticket codes, and rendering `APDC` at a user would be worse than
 * translating it.
 */
export const AGENCY_NAMES: Record<string, string> = {
  APDC: "Pastdue Credit",
  PDC: "Pastdue Credit",
  DigitalDRA: "Digital DRA",
  FirstLocate: "First Locate",
  CreditStyle: "Credit Style",
}

export const agencyName = (dca: string): string => AGENCY_NAMES[dca] ?? dca

const FALLBACK_COLOUR = "#9DA1A5"

/**
 * Resolved through `agencyName` so a legacy code and its display name cannot disagree.
 * Keyed directly, `FirstLocate` fell to the grey fallback while the label beside it read
 * "First Locate" - a bar in the wrong colour is worse than an unrecognised one, because
 * the legend then actively misidentifies it.
 */
export const agencyColour = (dca: string): string =>
  AGENCY_COLOURS[agencyName(dca)] ?? FALLBACK_COLOUR

/**
 * Agencies that must never be offered as a choice.
 *
 *  - `Moriarty` is in the registry as a real agency, but litigation is not a
 *    numbered placement and its position is owned by the Data-side pipeline, so it
 *    is not configurable here.
 *  - `Empty` is a sentinel row in `dca_configuration_v2`, not an agency. Retiring a
 *    step is done by removing every agency, not by allocating to `Empty`.
 */
/** Named once so the gate, the legend and this list cannot disagree on the spelling. */
export const MORIARTY = "Moriarty"

export const UNSELECTABLE_AGENCIES = [MORIARTY, "Empty"]
